import React, { useState } from "react";
import { STRING } from "@constants";
import {
  useAllUnitCategories,
  useAllUnitSizes,
  useRemoveUnitSizeById,
  useSizesByCategory,
} from "@api";
import { GenericDeleteDialog, GenericDetailsModal, Table } from "@components";
import { useGetUnitSizes } from "./useGetUnitSizes";
import { useManageUnitSizes } from "./useManageUnitSizes";
import { getUnitSizesDetails } from "./getUnitSizesDetails";
import { UnitSizeSideBar } from "./UnitSizeSideBar";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";

const T = STRING.UNIT_SIZE_DETAILS;

export function UnitSizes() {
  const { data: categories } = useAllUnitCategories();
  const { removeUnitSize } = useRemoveUnitSizeById();

  const [filterCategory, setFilterCategory] = useState<string | null>(null);

  const allSizesHook = useAllUnitSizes();
  const sizesByCategoryHook = useSizesByCategory(filterCategory ?? "", 10);

  const activeHook = filterCategory ? sizesByCategoryHook : allSizesHook;

  const { data, loading, loadMore, hasMore } = activeHook;

  const {
    selectedUnitSize,
    selectSizeToEdit,
    isDetailsModalOpen,
    handleCloseDetailsModal,
    handleRowClick,
    isSideBarOpen,
    toggleSideBar,
    handlePressDeleteButton,
    handleCloseDeleteDialog,
    unitSizeToDeleteId,
    selectedSizes,
    selectedCategory,
    setSelectedSizes,
    setSelectedCategory,
    handleAssignCategory,
  } = useManageUnitSizes();

  const { columns } = useGetUnitSizes({
    onEdit: selectSizeToEdit,
    onDelete: handlePressDeleteButton,
    categories: categories || [],
  });

  const { sections } = getUnitSizesDetails(selectedUnitSize);

  const handleDelete = async () => {
    await removeUnitSize(unitSizeToDeleteId);
  };

  return (
    <div className="overflow-hidden">
      <UnitSizeSideBar
        show={isSideBarOpen}
        editValues={selectedUnitSize}
        onClose={toggleSideBar}
      />

      <GenericDetailsModal
        visible={isDetailsModalOpen}
        onHide={handleCloseDetailsModal}
        sections={sections}
        header={T.MODAL_HEADER}
        noDataText={T.NO_DATA}
      />

      <GenericDeleteDialog
        visible={unitSizeToDeleteId !== ""}
        onClose={handleCloseDeleteDialog}
        onDelete={handleDelete}
        texts={{
          header: T.CONFIRM_DELETE,
          warning: T.DELETE_WARNING,
          success: T.DELETE_SUCCESS,
          error: T.DELETE_ERROR,
        }}
      />

      <Table
        columns={columns}
        data={data}
        title="Unit Sizes"
        onAddNew={toggleSideBar}
        onRowClicked={handleRowClick}
        loading={loading}
        loadMore={hasMore ? loadMore : undefined}
        selectionMode="multiple"
        selection={selectedSizes}
        onSelectionChange={(value) =>
          setSelectedSizes(Array.isArray(value) ? value : value ? [value] : [])
        }
        extraHeader={
          <div className="mb-4 flex align-items-center gap-4 flex-wrap">
            <div className="flex align-items-center gap-3">
              <span className="font-medium text-color">Filter by Status:</span>

              <Dropdown
                value={filterCategory}
                options={[
                  ...(categories || []).map((c) => ({
                    label: c.label,
                    value: c.id,
                  })),
                ]}
                showClear
                onChange={(e) => {
                  setFilterCategory(e.value);
                }}
                placeholder="Filter by Category"
                className="w-40"
              />
            </div>
            <div className="flex align-items-center gap-3">
              <span className="font-medium text-color">Assign category:</span>

              <Dropdown
                value={selectedCategory}
                options={(categories || []).map((c) => ({
                  label: c.label,
                  value: c.id,
                }))}
                showClear
                onChange={(e) => setSelectedCategory(e.value)}
                placeholder="Select Category"
                className="w-40"
              />
              <Button
                label="Assign to selected"
                onClick={handleAssignCategory}
                disabled={!selectedCategory || selectedSizes.length === 0}
              />
            </div>
          </div>
        }
      />
    </div>
  );
}
